import { db } from '../config/firebase';
import { collection, addDoc, getDocs, serverTimestamp, orderBy, query, where, doc, updateDoc, deleteDoc } from 'firebase/firestore';

const ACCOLADES_COLLECTION = 'accolades';

/**
 * Creates a new accolade.
 * @param {object} accoladeData - The data for the new accolade.
 * @param {string} accoladeData.title - The title of the accolade.
 * @param {string} accoladeData.description - The description of the achievement.
 * @param {string} accoladeData.characterId - The ID of the character recipient.
 * @param {string} accoladeData.characterName - The name of the character recipient.
 * @param {string} [accoladeData.imageUrl] - Optional image URL for the accolade.
 * @param {string} accoladeData.awardedByUserId - The ID of the user awarding the accolade.
 * @param {string} accoladeData.awardedByUserName - The name of the user awarding the accolade.
 * @param {string} accoladeData.category - The category of the accolade.
 * @param {string} accoladeData.rarity - The rarity level of the accolade.
 * @param {string} [accoladeData.campaignEpisodeId] - Optional campaign episode reference.
 * @param {string} [accoladeData.campaignEpisodeTitle] - Optional campaign episode title.
 * @returns {Promise<string>} The ID of the newly created accolade.
 */
export const createAccolade = async (accoladeData) => {
  if (!accoladeData.title || !accoladeData.description || !accoladeData.characterId || !accoladeData.awardedByUserId) {
    throw new Error('Title, description, character ID, and awarded by user ID are required to create an accolade.');
  }
    try {
    const docRef = await addDoc(collection(db, ACCOLADES_COLLECTION), {
      ...accoladeData,
      createdAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error("Error creating accolade: ", error);
    throw error;
  }
};

/**
 * Fetches all accolades, ordered by creation date (newest first).
 * @returns {Promise<Array<object>>} An array of accolade objects.
 */
export const getAllAccolades = async () => {
  try {
    const q = query(
      collection(db, ACCOLADES_COLLECTION),
      orderBy('createdAt', 'asc')
    );
    const querySnapshot = await getDocs(q);
    const accolades = [];
    querySnapshot.forEach((doc) => {
      accolades.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort accolades by creation date (newest first) on the client side
    accolades.sort((a, b) => {
      const aTime = a.createdAt?.seconds || 0;
      const bTime = b.createdAt?.seconds || 0;
      return bTime - aTime; // Descending order (newest first)
    });
    
    return accolades;
  } catch (error) {
    console.error("Error fetching accolades: ", error);
    throw error;
  }
};

/**
 * Fetches accolades for a specific character.
 * @param {string} characterId - The ID of the character.
 * @returns {Promise<Array<object>>} An array of accolade objects for the character.
 */
export const getAccoladesByCharacter = async (characterId) => {
  if (!characterId) {
    throw new Error('Character ID is required to fetch accolades.');
  }
  
  try {
    const q = query(
      collection(db, ACCOLADES_COLLECTION),
      where('characterId', '==', characterId),
      orderBy('createdAt', 'asc')
    );
    const querySnapshot = await getDocs(q);
    const accolades = [];
    querySnapshot.forEach((doc) => {
      accolades.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort accolades by creation date (newest first) on the client side
    accolades.sort((a, b) => {
      const aTime = a.createdAt?.seconds || 0;
      const bTime = b.createdAt?.seconds || 0;
      return bTime - aTime; // Descending order (newest first)
    });
    
    return accolades;
  } catch (error) {
    console.error("Error fetching accolades by character: ", error);
    throw error;
  }
};

/**
 * Fetches accolades by category.
 * @param {string} category - The category to filter by.
 * @returns {Promise<Array<object>>} An array of accolade objects in the category.
 */
export const getAccoladesByCategory = async (category) => {
  if (!category) {
    throw new Error('Category is required to fetch accolades.');
  }
  
  try {
    const q = query(
      collection(db, ACCOLADES_COLLECTION),
      where('category', '==', category),
      orderBy('createdAt', 'asc')
    );
    const querySnapshot = await getDocs(q);
    const accolades = [];
    querySnapshot.forEach((doc) => {
      accolades.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort accolades by creation date (newest first) on the client side
    accolades.sort((a, b) => {
      const aTime = a.createdAt?.seconds || 0;
      const bTime = b.createdAt?.seconds || 0;
      return bTime - aTime; // Descending order (newest first)
    });
    
    return accolades;
  } catch (error) {
    console.error("Error fetching accolades by category: ", error);
    throw error;
  }
};

/**
 * Updates an existing accolade.
 * @param {string} accoladeId - The ID of the accolade to update.
 * @param {object} updateData - The data to update.
 * @returns {Promise<void>}
 */
export const updateAccolade = async (accoladeId, updateData) => {
  if (!accoladeId) {
    throw new Error('Accolade ID is required to update an accolade.');
  }
  
  try {
    const accoladeRef = doc(db, ACCOLADES_COLLECTION, accoladeId);
    await updateDoc(accoladeRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error("Error updating accolade: ", error);
    throw error;
  }
};

/**
 * Deletes an accolade.
 * @param {string} accoladeId - The ID of the accolade to delete.
 * @returns {Promise<void>}
 */
export const deleteAccolade = async (accoladeId) => {
  if (!accoladeId) {
    throw new Error('Accolade ID is required to delete an accolade.');
  }
  
  try {
    const accoladeRef = doc(db, ACCOLADES_COLLECTION, accoladeId);
    await deleteDoc(accoladeRef);
  } catch (error) {
    console.error("Error deleting accolade: ", error);
    throw error;
  }
};

/**
 * Accolade categories for consistent categorization.
 */
export const ACCOLADE_CATEGORIES = [
  'Combat Excellence',
  'Roleplay',
  'Problem Solving',
  'Leadership',
  'Creativity',
  'Team Player',
  'Character Development',
  'Memorable Moment',
  'Strategic Thinking',
  'Humor',
  'Heroic Deed',
  'Other'
];

/**
 * Accolade rarity levels.
 */
export const ACCOLADE_RARITIES = [
  'Bronze',
  'Silver', 
  'Gold',
  'Legendary'
];
